import * as types from './types';

interface Location {
  pathname: string;
  search: string;
  hash: string;
  state?: unknown;
}

interface AppState {
  isLoaded: boolean;
  isLoading: boolean;
  isFetching: boolean;
  model: unknown;
  location: Location | null;
  nextLocation: Location | null;
  prevLocation: Location | null;
  error: {
    message: string;
    statusCode?: number;
    stack?: string;
  } | null;
  statusCode: number | null;
  loaded: boolean;
  userSessionExpired: boolean;
}

interface PageRequestAction {
  type: typeof types.PAGE_REQUEST;
  payload: {
    location: Location;
  };
}

interface PageSuccessAction {
  type: typeof types.PAGE_SUCCESS;
  payload: {
    model: unknown;
  };
}

interface PageErrorAction {
  type: typeof types.PAGE_ERROR;
  payload: {
    error: string | { message: string; status?: number; stack?: string };
  };
}

interface LocationChangeAction {
  type: typeof types.LOCATION_CHANGE;
  payload: {
    location: Location;
  };
}

interface InitializeSuccessAction {
  type: typeof types.INITIALIZE_SUCCESS;
}

interface SetIsLoadedAction {
  type: typeof types.SET_IS_LOADED;
  payload: {
    isLoaded: boolean;
  };
}

interface SetIsFetchingAction {
  type: typeof types.SET_IS_FETCHING;
  payload: {
    isFetching: boolean;
  };
}

interface UserSessionExpiredAction {
  type: typeof types.USER_SESSION_EXPIRED;
  payload: {
    didSessionExpire: boolean;
  };
}

type AppAction =
  | PageRequestAction
  | PageSuccessAction
  | PageErrorAction
  | LocationChangeAction
  | InitializeSuccessAction
  | SetIsLoadedAction
  | SetIsFetchingAction
  | UserSessionExpiredAction;

const initialState: AppState = {
  isLoaded: false,
  isLoading: false,
  isFetching: false,
  model: null,
  location: null,
  nextLocation: null,
  prevLocation: null,
  error: null,
  statusCode: null,
  loaded: false,
  userSessionExpired: false,
};

export default function app(
  state: AppState | undefined,
  action: AppAction,
): AppState {
  if (!state) {
    return initialState;
  }

  switch (action.type) {
    case types.LOCATION_CHANGE:
      return { ...state, isFetching: true, isLoading: true };
    case types.INITIALIZE_SUCCESS:
      return { ...state, loaded: true };
    case types.PAGE_REQUEST:
      return {
        ...state,
        isLoaded: false,
        isFetching: true,
        prevLocation: state.location,
        nextLocation: action.payload.location,
      };
    case types.PAGE_SUCCESS:
      return {
        ...state,
        isLoaded: true,
        isFetching: false,
        model: action.payload.model,
        error: null,
        prevLocation: state.location,
        location: state.nextLocation,
      };
    case types.PAGE_ERROR: {
      const error =
        typeof action.payload.error === 'string'
          ? { message: action.payload.error }
          : typeof action.payload.error === 'object' &&
            action.payload.error.message
          ? action.payload.error
          : { message: 'Unknown error' };
      return {
        ...state,
        isLoaded: true,
        isFetching: false,
        model: null,
        error: {
          message: error.message,
          statusCode: error.status,
          stack: error.stack,
        },
        location: state.nextLocation,
      };
    }
    case types.SET_IS_LOADED:
      return {
        ...state,
        isLoaded: action.payload.isLoaded,
        location: state.nextLocation,
      };
    case types.SET_IS_FETCHING:
      return { ...state, isFetching: action.payload.isFetching };
    case types.USER_SESSION_EXPIRED:
      return { ...state, userSessionExpired: action.payload.didSessionExpire };
    default:
      return state;
  }
}

export type { AppState, Location };
